import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useNuxtApp, useRuntimeConfig } from 'nuxt/app';
import { ORDER_STATUS_CREATED, ORDER_STATUS_WAITING_FOR_PAYMENT_IN_STORE } from '~~/common/config/OrderStatus';
import type { ExtendedPayload } from '~~/common/utils/helper';
import { getErrorNotification, setErrorNotification } from '~~/common/utils/helper';
import { isApiError } from '~~/common/utils/apiError';
import { PUSH_ADD_NEW_ITEM_TO_CART_PRE, PUSH_ADD_VOUCHER, PUSH_DELETE_ORDER, PUSH_REMOVE_CART_ITEM, PUSH_TRACK_CART_STATE, PUSH_TRUNCATE_CART } from '~~/common/plugins/aw-analytics';
import { M_DELIVERY_METHOD } from '~~/common/config/Modal';
import { useQueueStore } from '~~/common/stores/queue';
import { QES_SUPERSEDED_LOOPBLOCK } from '~~/common/config/QueueElementState';
import { useModalStore } from '~~/common/stores/modal';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { useCheckoutStore } from '~~/common/stores/checkout';
import { useUserStore } from '~~/common/stores/user';
import { useToasterStore } from '~~/common/stores/toaster';
import type { AxiosRequestConfig } from 'axios';
import type { DeepPartial, Schema, SchemaDealer } from '~~/common/types/api';

export type Cart = DeepPartial<Schema<'CartModel'>>;
export type CartSplit = DeepPartial<Schema<'CartSplitModel'>>;
export type CartPackage = DeepPartial<Schema<'CartPackageModel'>>;
export type CartItem = DeepPartial<Schema<'CartProductItemView'>>;
export type CartVoucher = DeepPartial<Schema<'CartVoucherModel'>>;
type OrderStatus = DeepPartial<SchemaDealer<'OrderStatus'>>;
export type ReturnUsedItemMap = Record<string, boolean>;

type AddToCartPayload = {
  id: string;
  objectType: string;
  data: { objectId?: string; stockType?: string; [key: string]: unknown };
  resolveOption?: string;
  fromSelfcare?: boolean;
};
/** `true`/`false` whether the item ended up in the cart; `null` on user cancel; `undefined` on the superseded 428-loop sentinel. */
type AddToCartResult = boolean | null | undefined;

type CompletedOrder = {
  orderIdent?: string;
  paymentMethod?: string;
  [key: string]: unknown;
};
type CompletedOrders = {
  food: CompletedOrder;
  nonFood: CompletedOrder;
};

type EditedOrder = {
  id?: number | null;
  strategy?: string;
} | null;

/** Per-action cache of the last HTTP status code, used to detect repeated 412 loops. */
type StatusCache = Partial<Record<
    | 'fetchOrder'
    | 'fetchCartMerge'
    | 'addToCart'
    | 'addCoupon'
    | 'updateCartItem'
    | 'startOrderModification'
    | 'removeCartItem'
    | 'removeAllUncreditableItem'
    | 'removeAllItem'
    | 'deleteOrder',
    number | undefined
>>;

/** Options accepted by `fetchOrder`/`fetchOrderRequest`. All optional; callers pass subsets. */
type FetchOrderOptions = {
  id?: string;
  resolveOption?: string;
  canRunSimultaneous?: boolean;
  canStartSecondAction?: boolean;
  fetchNonfoodCheckoutIfNFCartNotEmpty?: boolean;
  fetchFoodCheckout?: boolean;
  forceFetchNonfoodCheckout?: boolean;
  mode?: string | null;
  inDMModal?: boolean;
};

type FetchOrderResult = {
  fetchOrderErrorStatus: boolean;
  fetchOrderErrorResult?: {
    name: string;
    message: string | undefined;
    status?: number;
  };
  // `status` is read by callers (e.g. updateShopCheckoutData: `orderResult?.status === 428`). On the
  // in-DM-modal 428 branch fetchOrder returns getErrorNotification(), which spreads the raw axios error —
  // and AxiosError exposes an enumerable top-level `.status` (axios ≥1.7), so `status: 428` is present
  // there. It is not set on the normal result, hence optional.
  status?: number;
};

/**
 * `fetchOrder` resolves to a normal `FetchOrderResult` except on the in-DM-modal 428 branch,
 * where it returns the raw `getErrorNotification` payload (an `ExtendedPayload`) so the caller
 * can drive the delivery-method confirm modal.
 */
type FetchOrderReturn = FetchOrderResult | ExtendedPayload;

const cache: StatusCache = {};
let isPriceChangeMessageViewed = false;

let deletableStatusesCache: OrderStatus[] | undefined;
let orderStatusByIdCache: Record<string, OrderStatus> | undefined;

const strategies: Record<string, number> = {
  before_payment_modification: 1,
  after_payment_modification: 2,
};

export const useOrderStore = defineStore('order', () => {
  const nuxtApp = useNuxtApp();
  const config = useRuntimeConfig();

  const modalStore = useModalStore();
  const userInterfaceStore = useUserInterfaceStore();
  const queueStore = useQueueStore();
  const checkoutStore = useCheckoutStore();

  const order = ref<Cart>({
    'splits': [],
    'mergeNeeded': false,
    'merged': false,
  });
  const completedOrders = ref<CompletedOrders>({
    food: {},
    nonFood: {},
  });
  const returnUsedItem = ref<ReturnUsedItemMap>({});
  const cartMerge = ref<Cart>({});
  const statuses = ref<OrderStatus[]>([]);
  const orders = ref<unknown>(null);
  const editedOrder = ref<EditedOrder>(null);
  const editedOriginalOrder = ref<unknown>(null);

  const paymentData = ref<Record<string, unknown>>({});
  const ageConfirm = ref(false);
  const getCurrentOrderIsInProgress = ref(false);

  // getters
  const getStatusById = computed(() => {
    if (!orderStatusByIdCache && statuses.value.length) {
      orderStatusByIdCache = Object.fromEntries(statuses.value.map(s => [String(s.id), s]));
    }
    return orderStatusByIdCache || {};
  });
  const isStatusDeletable = computed(() => (statusId: number) => {
    if (!deletableStatusesCache) {
      deletableStatusesCache = statuses.value.filter(s => [ORDER_STATUS_CREATED, ORDER_STATUS_WAITING_FOR_PAYMENT_IN_STORE].includes(s.id ?? -1));
    }
    return deletableStatusesCache.find(s => s.id === statusId);
  });
  const getStaticInfosByCheckoutType = computed(() => {
    const ignoredKeysFilter = new Set([
      'blocked',
      'freeDeliveryInfo',
      'creditableButtons',
    ]);
    return (order.value?.splits || []).reduce<Record<string, Array<[string, unknown]>>>((acc, {
      checkoutType,
      infos,
    }) => {
      acc[checkoutType ?? ''] = (
        (Object.entries(infos || {})
          .filter(([k]) => !ignoredKeysFilter.has(k))
        )
      );
      return acc;
    }, {});
  });
  const getRawStaticInfo = computed(() => {
    return (order.value?.splits || []).reduce<Record<string, CartSplit['infos']>>((acc, {
      checkoutType,
      infos,
    }) => {
      acc[checkoutType ?? ''] = infos;
      return acc;
    }, {});
  });
  const getOrderItemDiscriminator = computed(() => ({ splits }: Cart, item: { uuid?: string }) => {
    return splits
      ?.flatMap(s => s.packages || [])
      ?.flatMap(({
        discriminator,
        items,
      }) => ({
        discriminator,
        items,
      }))
      ?.find(({ items }) => items?.find(({ uuid }) => item.uuid === uuid))?.discriminator;
  });
  const getCurrentFlattenedVouchers = computed<CartVoucher[]>(() => {
    return (order.value.splits || [])
      .flatMap(({ vouchers }) => vouchers || []);
  });
  const getFlattenedOrderItems = computed(() => ({ splits }: Cart): CartItem[] => {
    return (splits || [])
      .flatMap(s => s.packages || [])
      .flatMap(p => p.items || []);
  });
  // NOTE: it's more performant than getFlattenedOrderItems when current order is desired
  const getCurrentFlattenedOrderItems = computed(() => {
    return getFlattenedOrderItems.value(order.value);
  });
  const getOrderPackagesByObjectId = computed(() => ({ splits }: Cart, objectId: string) => {
    return (splits || [])
      .flatMap(s => s.packages || [])
      .filter(p => p.items?.some(i => i.objectId === objectId));
  });
  const getOrderItemSplit = computed(() => ({ splits }: Cart, item: { uuid?: string }) => {
    return splits
      ?.find(spl => spl.packages
        ?.flatMap(p => p.items || [])
        ?.some(({ uuid }) => item.uuid === uuid));
  });
  const getDeliveryModeAvailability = computed(() => (discrim: { servingMode?: string }, shipmentMethod = 'ALL') => {
    return [{
      value: 'store',
      label: 'awd.common.delivery_mode.selector.store.store',
      labelTfr: 'awd.common.delivery_mode.timeframe.store.store',
      labelSum: 'awd.common.delivery_mode.summary.store.store',
      disabled: discrim.servingMode === 'logistic',
      icon: {
        name: 'basket-with-handle-32',
        size: 32,
        fill: true,
      },
    }, {
      value: 'store',
      label: 'awd.common.delivery_mode.selector.store.logistic',
      labelTfr: 'awd.common.delivery_mode.timeframe.store.logistic',
      labelSum: 'awd.common.delivery_mode.summary.store.logistic',
      disabled: discrim.servingMode === 'store',
      icon: {
        name: 'timer-clock-32',
        size: 32,
        fill: true,
      },
    }, {
      value: 'home',
      label: `awd.common.delivery_mode.selector.home.${discrim.servingMode}`,
      labelTfr: `awd.common.delivery_mode.timeframe.home.${discrim.servingMode}`,
      labelSum: `awd.common.delivery_mode.summary.home.${discrim.servingMode}`,
      disabled: shipmentMethod === 'PACKET' && discrim.servingMode === 'store',
      icon: {
        name: `dealer-pick-${discrim.servingMode}-32`,
        size: 32,
        fill: true,
      },
    }, {
      value: 'courierapp',
      label: `awd.common.delivery_mode.selector.courierapp.${discrim.servingMode}`,
      labelTfr: `awd.common.delivery_mode.timeframe.courierapp.${discrim.servingMode}`,
      labelSum: `awd.common.delivery_mode.summary.courierapp.${discrim.servingMode}`,
      disabled: false,
      icon: {
        name: 'shipping-32',
        size: 32,
      },
    }, {
      value: 'extra_palett',
      label: `awd.common.delivery_mode.selector.extra_palett.${discrim.servingMode}`,
      labelTfr: `awd.common.delivery_mode.timeframe.extra_palett.${discrim.servingMode}`,
      labelSum: `awd.common.delivery_mode.summary.extra_palett.${discrim.servingMode}`,
      disabled: !['ALL', 'EXT_PALETT'].includes(shipmentMethod),
      icon: {
        name: `dealer-pick-${discrim.servingMode}-32`,
        size: 32,
        fill: true,
      },
    }];
  });
  const getModStrategy = computed(() => strategies[editedOrder.value?.strategy ?? ''] || 0);

  // actions
  function findCompletedOrderTypeByOrderIdent (orderIdent: string) {
    return completedOrders.value.food?.orderIdent === orderIdent ? completedOrders.value.food : completedOrders.value.nonFood?.orderIdent === orderIdent ? completedOrders.value.nonFood : null;
  }

  function setCompletedOrderToOnline (orderIdent: string) {
    const completedOrder = findCompletedOrderTypeByOrderIdent(orderIdent);
    if (completedOrder) {
      completedOrder.paymentMethod = 'online';
    }
  }

  function setCompletedOrderToCod (orderIdent: string) {
    const completedOrder = findCompletedOrderTypeByOrderIdent(orderIdent);
    if (completedOrder) {
      completedOrder.paymentMethod = 'onsite';
    }
  }

  function setOrder (newOrder: Cart) {
    order.value = newOrder;
  }

  const couponInfos = computed(() => order.value?.infos?.couponInfos);
  watch(couponInfos, (nextCouponInfos, prevCouponInfos) => {
    if (!nextCouponInfos) {
      return;
    }
    const toasterStore = useToasterStore();
    for (const couponId of Object.keys(nextCouponInfos)) {
      const nextEntries = nextCouponInfos[couponId];
      const prevEntries = prevCouponInfos?.[couponId];
      if (!Array.isArray(nextEntries)) {
        continue;
      }
      for (const nextEntry of nextEntries) {
        const prevEntry = prevEntries?.find(e => e.id === nextEntry.id);
        const wasActive = prevEntry?.isActive ?? null;
        const isNowActive = nextEntry.isActive;
        if (wasActive === isNowActive) {
          continue;
        }
        if (nextEntry.notificationText) {
          toasterStore.push({
            text: nextEntry.notificationText,
            type: isNowActive ? 'successGreen' : undefined,
            manualClose: true,
            closeAfter: 6000,
          });
        }
      }
    }
  });

  function setCompletedFoodOrder (split: CompletedOrder) {
    completedOrders.value.food = split;
  }

  function setCompletedNonFoodOrder (split: CompletedOrder) {
    completedOrders.value.nonFood = split;
  }

  function resetCompletedOrders () {
    completedOrders.value.food = {};
    completedOrders.value.nonFood = {};
  }

  function setReturnUsedItemFromOrder (newOrder: Cart) {
    const newReturnUsedItem: ReturnUsedItemMap = {};

    newOrder.splits?.forEach((split) => {
      split.packages?.forEach((splitPackage) => {
        splitPackage.items?.forEach((item) => {
          if (item.uuid && item.usedItem?.canReturn && item.usedItem.return) {
            newReturnUsedItem[item.uuid] = item.usedItem.return;
          }
        });
      });
    });

    returnUsedItem.value = { ...returnUsedItem.value, ...newReturnUsedItem };
  }

  function setReturnUsedItem (newReturnUsedItem: ReturnUsedItemMap) {
    returnUsedItem.value = { ...returnUsedItem.value, ...newReturnUsedItem };
  }

  function removeReturnUsedItem (returnUsedItemId: string) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete returnUsedItem.value[returnUsedItemId];
  }

  function setEditedOrder (payload: EditedOrder) {
    editedOrder.value = payload;
  }

  function removeEditedOrder () {
    editedOrder.value = null;
  }

  function setEditedOriginalOrder (payload: unknown) {
    editedOriginalOrder.value = payload;
  }

  function setPaymentData ({
    paymentData: newPaymentData,
    checkoutType,
  }: {
    paymentData?: unknown;
    checkoutType?: string;
  }) {
    if (newPaymentData && checkoutType) {
      paymentData.value[checkoutType] = newPaymentData;
    } else {
      paymentData.value = {};
    }
  }

  function setAgeConfirm (payload: boolean) {
    ageConfirm.value = payload;
  }

  async function fetchOrder ({
    id = 'current',
    resolveOption,
    canRunSimultaneous,
    canStartSecondAction,
    fetchNonfoodCheckoutIfNFCartNotEmpty = true,
    fetchFoodCheckout = true,
    forceFetchNonfoodCheckout = false,
    mode = null,
    inDMModal = false,
  }: FetchOrderOptions = {}): Promise<FetchOrderReturn> {
    if (import.meta.server) {
      return await fetchOrderRequest({
        id,
        resolveOption,
        fetchNonfoodCheckoutIfNFCartNotEmpty,
        fetchFoodCheckout,
        forceFetchNonfoodCheckout,
        canRunSimultaneous,
        canStartSecondAction,
        mode,
        inDMModal,
      });
    } else {
      userInterfaceStore.startLoading({ id: `fetch-order-${id}` });
      const result = await queueStore.add<FetchOrderOptions, FetchOrderReturn>({
        action: fetchOrderRequest,
        actionName: 'order/fetchOrderRequest',
        payload: {
          id,
          resolveOption,
          fetchNonfoodCheckoutIfNFCartNotEmpty,
          fetchFoodCheckout,
          forceFetchNonfoodCheckout,
          canRunSimultaneous,
          canStartSecondAction,
          mode,
          inDMModal,
        },
        canRunSimultaneous,
        canStartSecondAction,
      });
      // fetchOrderRequest never resolves with the 428-loop sentinel, but queue.add's
      // return type includes it; treat it as a benign empty result for the type.
      if (result === QES_SUPERSEDED_LOOPBLOCK) {
        return { fetchOrderErrorStatus: false };
      }
      return result;
    }
  }

  async function fetchOrderRequest ({
    id = 'current',
    fetchNonfoodCheckoutIfNFCartNotEmpty = true,
    fetchFoodCheckout = true,
    forceFetchNonfoodCheckout = false,
    resolveOption,
    canRunSimultaneous,
    canStartSecondAction,
    mode,
    inDMModal = false,
  }: FetchOrderOptions = {}): Promise<FetchOrderReturn> {
    // while (getCurrentOrderIsInProgress.value) {
    //   await new Promise(resolve => setTimeout(resolve, 256));
    // }

    const {
      $api,
      $awAnalytics,
      $logger,
      $awSFMCClient,
    } = nuxtApp;

    getCurrentOrderIsInProgress.value = true;

    try {
      // TODO: kell nekünk valamire a summary végpont?
      const modeMap: Record<string, string> = {
        BASKET: '',
        MINICART: '/unsplit',
      };
      const modePart = __AW_IS_SHOP__ ? modeMap[mode || 'BASKET'] : modeMap.BASKET;
      const r = await $api.get<Cart>(`/orders/${id}/cart${modePart}`, addResolveOptionsHeader(resolveOption));
      cache.fetchOrder = r?.status;
      const p: unknown[] = [];
      p.push($awAnalytics[PUSH_TRACK_CART_STATE]({
        newCart: r.data,
        oldCart: order.value,
      }));
      setOrder(r.data);
      setReturnUsedItemFromOrder(r.data);
      if (r.data.merged) {
        $awSFMCClient[PUSH_TRACK_CART_STATE]({ cart: r.data });
      }
      if (forceFetchNonfoodCheckout || fetchNonfoodCheckoutIfNFCartNotEmpty && r.data.splits?.some(s => s.checkoutType === 'NONFOOD')) {
        p.push(checkoutStore.fetchCheckout({
          type: 'NONFOOD',
          canRunSimultaneous,
          canStartSecondAction,
        }));
      }
      if (fetchFoodCheckout) {
        p.push(checkoutStore.fetchCheckout({
          type: 'FOOD',
          canRunSimultaneous,
          canStartSecondAction,
        }));
      }
      await Promise.all(p);
      return {
        fetchOrderErrorStatus: false,
      };
    } catch (error) {
      const previous = cache.fetchOrder;
      const errorObj = isApiError(error) ? error : undefined;
      cache.fetchOrder = errorObj?.response?.status;

      if (previous === errorObj?.response?.status && previous === 412) {
        return {
          fetchOrderErrorStatus: true,
        };
      }

      if (inDMModal && errorObj?.response?.status === 428) {
        return getErrorNotification(nuxtApp, error, true, {
          storeAction: fetchOrder,
          storeActionParameters: {
            id,
            fetchNonfoodCheckoutIfNFCartNotEmpty,
            fetchFoodCheckout,
            resolveOption,
            canRunSimultaneous,
            canStartSecondAction,
            mode,
            inDMModal,
          },
        });
      } else {
        // setErrorNotification() method `closable` parameter depends on whether M_DELIVERY_METHOD opened or not
        const {
          name,
          message,
        } = await setErrorNotification({ nuxtApp, error, closable: modalStore.activeGenericModal.type === M_DELIVERY_METHOD }) || {};
        if (name === 'accept' && message) {
          await fetchOrder({
            id,
            fetchNonfoodCheckoutIfNFCartNotEmpty,
            fetchFoodCheckout,
            forceFetchNonfoodCheckout,
            canRunSimultaneous,
            canStartSecondAction,
            mode,
            inDMModal,
            resolveOption: message,
          });
        } else if (name === 'cancel') {
          // do nothing
        } else {
          queueStore.removeAllElementFromQueue(false);
          $logger.error(error);
        }
        return {
          fetchOrderErrorStatus: true,
          fetchOrderErrorResult: {
            name: name ?? '',
            message,
            status: errorObj?.response?.status,
          },
        };
      }
    } finally {
      getCurrentOrderIsInProgress.value = false;
      userInterfaceStore.endLoading({ id: `fetch-order-${id}` });
    }
  }

  async function fetchCartMerge (resolveOption?: string) {
    const {
      $api,
      $logger,
      $awSFMCClient,
    } = nuxtApp;
    try {
      const r = await $api.get<Cart>('/orders/current/cart/merge', (resolveOption
        ? {
          headers: {
            'X-Error-Resolve-Option': resolveOption,
          },
        }
        : {}));
      cache.fetchCartMerge = r?.status;
      cartMerge.value = r.data;
      if (r.data.merged) {
        $awSFMCClient[PUSH_TRACK_CART_STATE]({ cart: r.data });
      }
    } catch (error) {
      const previous = cache.fetchCartMerge;
      const errorObj = isApiError(error) ? error : undefined;
      cache.fetchCartMerge = errorObj?.response?.status;

      if (previous === errorObj?.response?.status && previous === 412) {
        return;
      }

      const {
        name,
        message,
      } = await setErrorNotification({ nuxtApp, error, closable: false }) || {};
      if (name === 'accept' && message) {
        fetchCartMerge(message);
      } else if (name === 'cancel') {
        // do nothing
      } else {
        $logger.error(error);
      }
    }
  }

  async function fetchStatuses () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const { data } = await $api.get<OrderStatus[]>('statuses');
      statuses.value = data;
    } catch (e) {
      $logger.error(e);
    }
  }

  async function fetchOrderModification () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    if (__AW_IS_SHOP__) {
      try {
        const result = await $api.$get<{ orderId?: number; strategy?: string }>('/order_modification');
        setEditedOrder({
          id: result?.orderId,
          strategy: result?.strategy,
        });

        const originalOrder = await $api.$get<Schema<'OrderModel'>>(`/orders/${result.orderId}`);
        setEditedOriginalOrder(originalOrder);

        fetchOrder({
          id: 'current',
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });
      } catch (error) {
        // if error.response.status === 404 or 403 we don't have pending order modification or user is not logged in, this is not error
        const status = isApiError(error) ? error.response?.status : undefined;
        if (status === 404 || status === 403) {
          setEditedOrder(null);
          setEditedOriginalOrder(null);
        } else {
          $logger.error(error);
          await setErrorNotification({ nuxtApp, error });
        }
      }
    }
  }

  async function checkPriceChange ({
    objectId,
    objectType,
    referenceId,
  }: {
    objectId: string;
    objectType: string;
    referenceId: string;
  }) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      if (!config.public.isPriceChangeChecked || isPriceChangeMessageViewed) {
        return;
      }
      await $api.get(`orders/${referenceId}/check_price_change/${objectType}/${objectId}`);
    } catch (error) {
      if (isApiError(error) && error.response?.status === 409) {
        setErrorNotification({ nuxtApp, error, closable: false, forceModal: true });
        isPriceChangeMessageViewed = true;
      } else {
        $logger.error(error);
        setErrorNotification({ nuxtApp, error });
      }
    }
  }

  async function addToCart ({
    id,
    objectType,
    data,
    resolveOption,
    fromSelfcare,
    /* fromBasket = false, */
  }: AddToCartPayload): Promise<AddToCartResult> {
    userInterfaceStore.startLoading({ id: `add-to-cart-${data.objectId}-${data.stockType}` });
    const result = await queueStore.add<AddToCartPayload, AddToCartResult>({
      action: addToCartRequest,
      actionName: 'order/addToCartRequest',
      payload: {
        id,
        objectType,
        data,
        resolveOption,
        fromSelfcare,
      },
      canStartSecondAction: true,
    });
    // A superseded 428-loop element resolves with a truthy sentinel symbol;
    // map it back to undefined so callers' truthiness checks treat it as "not added".
    if (result === QES_SUPERSEDED_LOOPBLOCK) {
      return undefined;
    }
    return result;
  }

  async function addToCartRequest ({
    id,
    objectType,
    data,
    resolveOption,
    fromSelfcare,
  }: AddToCartPayload): Promise<AddToCartResult> {
    const {
      $api,
      $awAnalytics,
      $logger,
      $awSFMCClient,
    } = nuxtApp;

    try {
      try {
        await $awAnalytics[PUSH_ADD_NEW_ITEM_TO_CART_PRE]({ objectType });
      } catch (error) {
        $logger.error(error);
      }
      const {
        status: cartResponseStatus,
        data: cartResponseData,
      } = await $api.post<Cart>(`orders/${id}/cart/cartables/${objectType}`,
        data,
        (resolveOption
          ? {
            headers: {
              'X-Error-Resolve-Option': resolveOption,
            },
          }
          : {}
        ),
      );
      cache.addToCart = cartResponseStatus;
      let analytics;
      try {
        analytics = $awAnalytics[PUSH_TRACK_CART_STATE]({
          newCart: cartResponseData,
          oldCart: order.value,
          newObject: {
            objectType,
            objectId: data?.objectId ?? '',
          },
        });
        $awSFMCClient[PUSH_TRACK_CART_STATE]({ cart: cartResponseData });
      } catch (error) {
        $logger.error(error);
      }
      if (cartResponseData.splits?.some(split => split.checkoutType === 'NONFOOD')) {
        await checkoutStore.fetchCheckout({
          type: 'NONFOOD',
          canRunSimultaneous: true,
        });
      }
      // Here we can use the splitted cart data from the response, because when we hit this button, the minicart is closed, and then when we open it again the minicart will fetch the unsplitted data for itself (this way we can save cart resource
      setOrder(cartResponseData);
      setReturnUsedItemFromOrder(cartResponseData);

      const isInCart = (objectType === 'variant'
        ? getCurrentFlattenedOrderItems.value?.some(i => i.variant?.id === parseInt(data?.objectId ?? ''))
        : true // NOTE: for other types like shoppingLists/orders we assume it worked.
      );

      await analytics;
      return isInCart;
    } catch (error) {
      userInterfaceStore.endLoading({ id: `add-to-cart-${data.objectId}-${data.stockType}` });

      const previous = cache.addToCart;
      const errorObj = isApiError(error) ? error : undefined;
      cache.addToCart = errorObj?.response?.status;

      if (previous === errorObj?.response?.status && previous === 412) {
        return false;
      }

      queueStore.checkPreviousErrorAndResolveIfNeeded({ previous: previous ?? 0, error, actionName: 'order/addToCartRequest', errorScope: 'coldParcel', httpCode: 428 });

      const {
        name,
        message,
      } = await setErrorNotification({ nuxtApp, error, closable: (errorObj?.response?.status === 428 ? true : !!fromSelfcare), fromSelfcare, enableTitleMerge: false }) || {};
      if (name === 'accept' && message) {
        return await addToCart({
          id,
          objectType,
          data,
          fromSelfcare,
          resolveOption: message,
        });
      } else if (name === 'cancel') {
        // do nothing
        return null;
      } else {
        queueStore.removeAllElementFromQueue(false);
        $logger.error(error);
      }
    } finally {
      userInterfaceStore.endLoading({ id: `add-to-cart-${data.objectId}-${data.stockType}` });
    }
  }

  async function addCoupon ({
    id,
    objectType,
    objectId,
    resolveOption,
  }: {
    id: string;
    objectType: string;
    objectId: string;
    resolveOption?: string;
  }) {
    userInterfaceStore.startLoading({ id: `add-to-coupon-${objectId}` });
    return await queueStore.add({
      action: addCouponRequest,
      actionName: 'order/addCouponRequest',
      payload: {
        id,
        objectType,
        objectId,
        resolveOption,
      },
      canStartSecondAction: true,
    });
  }

  async function addCouponRequest ({
    id,
    objectType,
    objectId,
    resolveOption,
  }: {
    id: string;
    objectType: string;
    objectId: string;
    resolveOption?: string;
  }) {
    const {
      $api,
      $awAnalytics,
      $logger,
    } = nuxtApp;

    try {
      const r = await $api.post<Cart>(`orders/${id}/cart/cartables/${objectType}`, { objectId }, (resolveOption
        ? {
          headers: {
            'X-Error-Resolve-Option': resolveOption,
          },
        }
        : {}));
      cache.addCoupon = r.status;
      setOrder(r.data);
      setReturnUsedItemFromOrder(r.data);
      if (r.data.splits?.some(split => split.checkoutType === 'NONFOOD')) {
        await checkoutStore.fetchCheckout({
          type: 'NONFOOD',
          canRunSimultaneous: true,
        });
      }
      try {
        $awAnalytics[PUSH_ADD_VOUCHER]({ objectId });
      } catch (error) {
        $logger.error(error);
      }
      return true;
    } catch (error) {
      userInterfaceStore.endLoading({ id: `add-to-coupon-${objectId}` });

      const previous = cache.addCoupon;
      const errorObj = isApiError(error) ? error : undefined;
      cache.addCoupon = errorObj?.response?.status;
      if (previous === errorObj?.response?.status && previous === 412) {
        return false;
      }
      const {
        name,
        message,
      } = await setErrorNotification({ nuxtApp, error }) || {};
      if (name === 'accept' && message) {
        await addCoupon({
          id,
          objectType,
          objectId,
          resolveOption: message,
        });
      } else if (name === 'cancel') {
        // do nothing
      } else {
        queueStore.removeAllElementFromQueue(false);
        $logger.error(error);
      }
      return false;
    } finally {
      userInterfaceStore.endLoading({ id: `add-to-coupon-${objectId}` });
    }
  }

  async function updateCartItemQuantity ({
    uuid,
    quantity,
    quantityType,
    fromBasket = false,
  }: {
    uuid: string;
    quantity: number;
    quantityType: string;
    fromBasket?: boolean;
  }) {
    await updateCartItem({
      uuid,
      fromBasket,
      data: {
        quantity: {
          quantity,
          type: ['db', 'piece', 'szt.'].includes(quantityType) ? 'piece' : 'weight',
        },
      },
    });
  }

  async function updateCartItemReturnUsed ({
    uuid,
    returnUsed,
    fromBasket,
  }: {
    uuid: string;
    returnUsed: boolean;
    fromBasket?: boolean;
  }) {
    await updateCartItem({
      uuid,
      fromBasket,
      data: {
        usedItem: {
          return: returnUsed,
        },
      },
    });
  }

  async function updateCartItemStickerUsed ({
    uuid,
    selectedOption,
    fromBasket,
  }: {
    uuid: string;
    selectedOption: unknown;
    fromBasket?: boolean;
  }) {
    await updateCartItem({
      uuid,
      fromBasket,
      data: {
        stickerRedemption: selectedOption,
      },
    });
  }

  async function updateCartItem ({
    uuid,
    data,
    resolveOption,
    fromBasket = false,
  }: {
    uuid: string;
    data: { barcode?: string; [key: string]: unknown };
    resolveOption?: string;
    fromBasket?: boolean;
  }) {
    userInterfaceStore.startLoading({ id: `update-cart-item-${uuid}` });
    const result = await queueStore.add({
      action: updateCartItemRequest,
      actionName: 'order/updateCartItemRequest',
      payload: {
        uuid,
        data,
        resolveOption,
        fromBasket,
      },
      canStartSecondAction: true,
    });
    // A superseded 428-loop element resolves with a truthy sentinel symbol;
    // map it back to undefined so callers' truthiness checks treat it as "not updated".
    if (result === QES_SUPERSEDED_LOOPBLOCK) {
      return undefined;
    }
    return result;
  }

  async function updateCartItemRequest ({
    uuid,
    data,
    resolveOption,
    fromBasket,
  }: {
    uuid: string;
    data: { barcode?: string; [key: string]: unknown };
    resolveOption?: string;
    fromBasket?: boolean;
  }) {
    const {
      $api,
      $awAnalytics,
      $logger,
      $awSFMCClient,
    } = nuxtApp;

    try {
      const r = await $api.patch<Cart>(`orders/current/cart/items/${uuid}${data.barcode ? '/yellowBarcode' : ''}`, data, {
        headers: {
          'Content-Type': 'application/merge-patch+json', ...(resolveOption ? { 'X-Error-Resolve-Option': resolveOption } : {}),
        },
      });
      cache.updateCartItem = r.status;

      const analytics = $awAnalytics[PUSH_TRACK_CART_STATE]({
        newCart: r.data,
        oldCart: order.value,
        modifiedObject: { uuid },
      });
      $awSFMCClient[PUSH_TRACK_CART_STATE]({ cart: r.data });
      if (fromBasket) {
        setOrder(r.data);
        setReturnUsedItemFromOrder(r.data);
      } else {
        await fetchOrderRequest({
          id: 'current',
          resolveOption,
          fetchNonfoodCheckoutIfNFCartNotEmpty: true,
          fetchFoodCheckout: true,
          canRunSimultaneous: true,
          canStartSecondAction: true,
          mode: 'MINICART',
        });
      }
      if (r.data.splits?.some(split => split.checkoutType === 'NONFOOD')) {
        await checkoutStore.fetchCheckout({
          type: 'NONFOOD',
          canRunSimultaneous: true,
        });
      }
      await analytics;
    } catch (error) {
      userInterfaceStore.endLoading({ id: `update-cart-item-${uuid}` });
      const previous = cache.updateCartItem;
      const errorObj = isApiError(error) ? error : undefined;
      cache.updateCartItem = errorObj?.response?.status;
      if (previous === errorObj?.response?.status && previous === 412) {
        return false;
      }

      queueStore.checkPreviousErrorAndResolveIfNeeded({ previous: previous ?? 0, error, actionName: 'order/updateCartItemRequest', errorScope: 'coldParcel', httpCode: 428 });

      const {
        name,
        message,
      } = await setErrorNotification({ nuxtApp, error }) || {};
      if (name === 'accept' && message) {
        await updateCartItem({
          uuid,
          data,
          fromBasket,
          resolveOption: message,
        });
      } else if (name === 'cancel') {
        await fetchOrder({
          id: 'current',
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });
      } else {
        queueStore.removeAllElementFromQueue(false);

        $logger.error(error);
        await fetchOrder({
          id: 'current',
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });
      }
    } finally {
      userInterfaceStore.endLoading({ id: `update-cart-item-${uuid}` });
    }
  }

  async function updateCartItemYellowBarcode ({
    uuid,
    barcode,
  }: {
    uuid: string;
    barcode: string;
  }) {
    userInterfaceStore.startLoading({ id: `update-cart-item-${uuid}` });
    const data = {
      barcode: barcode,
    };
    return await queueStore.add({
      action: updateCartItemRequest,
      actionName: 'order/updateCartItemRequest',
      payload: {
        uuid,
        data,
      },
      canStartSecondAction: true,
    });
  }

  /**
   * @returns whether modification is started
   */
  async function startOrderModification ({
    orderId,
    resolveOption,
  }: {
    orderId: number | string;
    resolveOption?: string;
  }): Promise<boolean> {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const r = await $api.post('/order_modification', { orderId }, {
        headers: {
          ...(resolveOption ? { 'X-Error-Resolve-Option': resolveOption } : {}),
        },
      });
      cache.startOrderModification = r.status;
      await fetchOrderModification();
      return true;
    } catch (error) {
      const previous = cache.startOrderModification;
      const errorObj = isApiError(error) ? error : undefined;
      cache.startOrderModification = errorObj?.response?.status;
      if (previous === errorObj?.response?.status && previous === 412) {
        return false;
      }
      const {
        name,
        message,
      } = await setErrorNotification({ nuxtApp, error }) || {};
      if (name === 'accept' && message) {
        return await startOrderModification({
          orderId,
          resolveOption: message,
        });
      } else if (name === 'cancel') {
        // do nothing
      } else {
        $logger.error(error);
      }
      return false;
    }
  }

  async function removeCartItem ({
    uuid,
    fromBasket,
    resolveOption,
  }: {
    uuid: string;
    fromBasket?: boolean;
    resolveOption?: string;
  }) {
    userInterfaceStore.startLoading({ id: `remove-cart-item-${uuid}` });
    return await queueStore.add({
      action: removeCartItemRequest,
      actionName: 'order/removeCartItemRequest',
      payload: {
        uuid,
        fromBasket,
        resolveOption,
      },
      canStartSecondAction: true,
    });
  }

  async function removeCartItemRequest ({
    uuid,
    fromBasket = false,
    resolveOption,
  }: {
    uuid: string;
    fromBasket?: boolean;
    resolveOption?: string;
  }) {
    const {
      $api,
      $awAnalytics,
      $logger,
      $awSFMCClient,
    } = nuxtApp;

    try {
      let callWhenSucceeds = () => {
      };
      try {
        callWhenSucceeds = $awAnalytics[PUSH_REMOVE_CART_ITEM]({
          uuid,
          newQuantity: {
            unit: 'piece',
            value: 0,
          },
        });
      } catch (error) {
        $logger.error(error);
      }

      const r = await $api.delete(`orders/current/cart/items/${uuid}`, addResolveOptionsHeader(resolveOption));
      cache.removeCartItem = r.status;

      try {
        callWhenSucceeds();
      } catch (error) {
        $logger.error(error);
      }

      await fetchOrder({
        id: 'current',
        canStartSecondAction: true,
        canRunSimultaneous: true,
        mode: fromBasket ? 'BASKET' : 'MINICART',
      });
      $awSFMCClient[PUSH_TRACK_CART_STATE]({ cart: order.value });
    } catch (error) {
      userInterfaceStore.endLoading({ id: `remove-cart-item-${uuid}` });
      const previous = cache.removeCartItem;
      const errorObj = isApiError(error) ? error : undefined;
      cache.removeCartItem = errorObj?.response?.status;
      if (previous === errorObj?.response?.status && previous === 412) {
        return false;
      }
      const {
        name,
        message,
      } = await setErrorNotification({ nuxtApp, error }) || {};
      if (name === 'accept' && message) {
        await removeCartItem({
          uuid,
          fromBasket,
          resolveOption: message,
        });
      } else if (name === 'cancel') {
        await fetchOrder({
          id: 'current',
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });
      } else {
        queueStore.removeAllElementFromQueue(false);

        $logger.error(error);
        await fetchOrder({
          id: 'current',
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });
      }
    } finally {
      userInterfaceStore.endLoading({ id: `remove-cart-item-${uuid}` });
    }
  }

  async function removeAllUncreditableItem (payload: {
    baremId?: string;
    resolveOption?: string;
    isCartFetchNeeded?: boolean;
  }) {
    userInterfaceStore.startLoading({ id: 'remove-all-uncreditable-item' });
    return await queueStore.add({
      action: removeAllUncreditableItemRequest,
      actionName: 'order/removeAllUncreditableItemRequest',
      payload,
      canStartSecondAction: true,
    });
  }

  async function removeAllUncreditableItemRequest ({
    baremId,
    resolveOption,
    isCartFetchNeeded = true,
  }: {
    baremId?: string;
    resolveOption?: string;
    isCartFetchNeeded?: boolean;
  } = {}) {
    const {
      $api,
      $logger,
      $awSFMCClient,
    } = nuxtApp;
    try {
      try {
        const r = await $api.delete(
          `/orders/current/cart/delete-uncreditables/${baremId}`,
          addResolveOptionsHeader(resolveOption),
        );
        cache.removeAllUncreditableItem = r.status;
      } catch (error) {
        const previous = cache.removeAllUncreditableItem;
        const errorObj = isApiError(error) ? error : undefined;
        cache.removeAllUncreditableItem = errorObj?.response?.status;
        if (previous === errorObj?.response?.status && previous === 412) {
          return false;
        }
        const {
          name,
          message,
        } = await setErrorNotification({ nuxtApp, error, enableTitleMerge: false }) || {};
        if (name === 'accept' && message) {
          await removeAllUncreditableItem({
            baremId,
            resolveOption: message,
            isCartFetchNeeded: false,
          });
        } else if (name === 'cancel') {
          // do nothing
        } else {
          $logger.error(error);
          queueStore.removeAllElementFromQueue(false);
        }
      }
      if (isCartFetchNeeded) {
        await fetchOrder({
          id: 'current',
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });
        $awSFMCClient[PUSH_TRACK_CART_STATE]({ cart: order.value });
      }
    } catch (error) {
      $logger.error(error);
      queueStore.removeAllElementFromQueue(false);
    } finally {
      userInterfaceStore.endLoading({ id: 'remove-all-uncreditable-item' });
    }
  }

  async function getAllUncreditableItem (baremId: number) {
    const {
      $api,
      $logger,
    } = nuxtApp;

    try {
      return await $api.$get<Schema<'CartModel.CartProductItem'>[]>(`/orders/current/cart/uncreditables/${baremId}`);
    } catch (error) {
      $logger.error(error);
    }
  }

  async function removeAllItem (checkoutType: string) {
    const {
      $awAnalytics,
      $logger,
    } = nuxtApp;
    userInterfaceStore.startLoading({ id: `remove-all-item-${checkoutType}` });
    try {
      $awAnalytics[PUSH_TRUNCATE_CART]({ checkoutType });
    } catch (error) {
      $logger.error(error);
    }
    return await queueStore.add({
      action: removeAllItemRequest,
      actionName: 'order/removeAllItemRequest',
      payload: { checkoutType },
      canStartSecondAction: true,
    });
  }

  async function removeAllItemRequest ({ checkoutType }: { checkoutType: string }) {
    const {
      $api,
      $logger,
      $awSFMCClient,
    } = nuxtApp;
    try {
      const r = await $api.delete('orders/current/cart/splits?checkoutTypes%5B%5D=' + checkoutType);
      cache.removeAllItem = r.status;

      await fetchOrder({
        id: 'current',
        canStartSecondAction: true,
        canRunSimultaneous: true,
      });
      $awSFMCClient[PUSH_TRACK_CART_STATE]({ cart: order.value });
    } catch (error) {
      const previous = cache.removeAllItem;
      const errorObj = isApiError(error) ? error : undefined;
      cache.removeAllItem = errorObj?.response?.status;
      if (previous === errorObj?.response?.status && previous === 412) {
        return false;
      }
      const {
        name,
        message,
      } = await setErrorNotification({ nuxtApp, error }) || {};
      if (name === 'accept' && message) {
        await removeAllItem(checkoutType);
      } else if (name === 'cancel') {
        await fetchOrder({
          id: 'current',
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });
      } else {
        queueStore.removeAllElementFromQueue(false);
        $logger.error(error);
        await fetchOrder({
          id: 'current',
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });
      }
    } finally {
      userInterfaceStore.endLoading({ id: `remove-all-item-${checkoutType}` });
    }
  }

  async function deleteOrder (id: string, resolveOption?: string): Promise<boolean> {
    const {
      $api,
      $awAnalytics,
      $logger,
    } = nuxtApp;
    const userStore = useUserStore();

    try {
      const r = await $api.delete(`/orders/${id}`, addResolveOptionsHeader(resolveOption));
      cache.deleteOrder = r.status;
      try {
        $awAnalytics[PUSH_DELETE_ORDER]({
          user: userStore.data,
          orderIdent: id,
        });
      } catch (error) {
        $logger.error(error);
      }
      return true;
    } catch (error) {
      const previous = cache.removeAllItem;
      const errorObj = isApiError(error) ? error : undefined;
      cache.removeAllItem = errorObj?.response?.status;
      if (previous === errorObj?.response?.status && previous === 412) {
        return false;
      }
      const {
        name,
        message,
      } = await setErrorNotification({ nuxtApp, error }) || {};
      if (name === 'accept' && message) {
        return deleteOrder(id, message);
      } else if (name === 'cancel') {
        // do nothing
      } else {
        $logger.error(error);
      }
      return false;
    }
  }

  async function deleteOrderModification () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      await $api.delete('/order_modification');
      await fetchOrderModification();
      await fetchOrder({
        id: 'current',
        canStartSecondAction: true,
        canRunSimultaneous: true,
      });
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  return {
    order,
    completedOrders,
    returnUsedItem,
    cartMerge,
    statuses,
    orders,
    editedOrder,
    editedOriginalOrder,
    paymentData,
    ageConfirm,
    getCurrentOrderIsInProgress,
    getStatusById,
    isStatusDeletable,
    getStaticInfosByCheckoutType,
    getRawStaticInfo,
    getOrderItemDiscriminator,
    getCurrentFlattenedVouchers,
    getFlattenedOrderItems,
    getCurrentFlattenedOrderItems,
    getOrderPackagesByObjectId,
    getOrderItemSplit,
    getDeliveryModeAvailability,
    getModStrategy,
    findCompletedOrderTypeByOrderIdent,
    setCompletedOrderToOnline,
    setCompletedOrderToCod,
    setOrder,
    setCompletedFoodOrder,
    setCompletedNonFoodOrder,
    resetCompletedOrders,
    setReturnUsedItemFromOrder,
    setReturnUsedItem,
    removeReturnUsedItem,
    setEditedOrder,
    removeEditedOrder,
    setPaymentData,
    setAgeConfirm,
    fetchOrder,
    fetchOrderRequest,
    fetchCartMerge,
    fetchStatuses,
    fetchOrderModification,
    checkPriceChange,
    addToCart,
    addToCartRequest,
    addCoupon,
    addCouponRequest,
    updateCartItemQuantity,
    updateCartItemReturnUsed,
    updateCartItemStickerUsed,
    updateCartItem,
    updateCartItemYellowBarcode,
    updateCartItemRequest,
    startOrderModification,
    removeCartItem,
    removeCartItemRequest,
    removeAllUncreditableItem,
    removeAllUncreditableItemRequest,
    removeAllItem,
    removeAllItemRequest,
    deleteOrder,
    deleteOrderModification,
    getAllUncreditableItem,
  };
});

function addResolveOptionsHeader (resolveOption?: string): AxiosRequestConfig {
  return (resolveOption
    ? {
      headers: {
        'X-Error-Resolve-Option': resolveOption,
      },
    }
    : {}
  );
}
